import React, { useMemo, useRef } from 'react';
import styled from 'styled-components/macro';
import { line, area, curveMonotoneX } from 'd3-shape';
import last from 'lodash.last';

import getMargins from 'components/data-visualization/LineChart/helpers/getMargins';
import getEntityKey from 'helpers/getEntityKey';
import generateRandomHash from 'helpers/generateRandomHash';
import { useTimeScale } from 'components/data-visualization/utils/createTimeScale';
import {
  useLinearScale,
  createLinearScale,
} from 'components/data-visualization/utils/createLinearScale';
import { useBandScale } from 'components/data-visualization/utils/createBandScale';
import { round } from 'components/data-visualization/LineChart/helpers/math';

import { isLineChartPathDefined } from 'components/Overview/LineChart/utils/pathDefinitions';

import AxesInStrictDesign from 'components/data-visualization/LineChart/AxesInStrictDesign';
import Hitbox from 'components/data-visualization/LineChart/Hitbox';
import ThisWeek from 'components/data-visualization/LineChart/ThisWeek';
import Tracker from 'components/data-visualization/LineChart/Tracker';
import ChartLines from 'components/data-visualization/LineChart/ChartLines';
import { FormatType } from 'helpers/formatNumber';

const InnerGroup = styled('g')`
  overflow: visible;
  pointer-events: none;
  user-select: none;
`;

const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

interface ILineChartProps {
  colorScale: any;
  curve: any;
  dataType: FormatType;
  entities: any;
  fillFromLastNonNullValue: any;
  hasNoData: boolean;
  hasComplementary: boolean;
  handleDragChange: any;
  handleMouseClick: any;
  handleMouseHover: any;
  isComplementary: boolean;
  label: any;
  parentHeight: number;
  parentWidth: number;
  period: any;
  shouldTransitionIn: boolean;
  showTooltip: boolean;
  selectedEntities: any[];
  temporarySelectedEntities: any[];
  tooltipRoot: any;
  isStrictDesign: boolean;
  isSmallScreen: boolean;
}

const LineChart: React.FC<ILineChartProps> = (props) => {
  const {
    colorScale,
    curve = curveMonotoneX,
    dataType = 'number',
    entities,
    fillFromLastNonNullValue,
    hasNoData,
    hasComplementary,
    handleDragChange = noop,
    handleMouseClick = noop,
    handleMouseHover = noop,
    isComplementary,
    label,
    parentHeight = 960,
    parentWidth = 384,
    period = 'day',
    shouldTransitionIn,
    showTooltip,
    selectedEntities,
    temporarySelectedEntities,
    tooltipRoot,
    isStrictDesign,
    isSmallScreen,
  } = props;

  /**
   * Instance variables
   */
  const randomHash = useRef(generateRandomHash());
  const clipPathId = useRef(
    `clip-sides-${getEntityKey(entities?.[0])}-${randomHash.current}`
  );
  const trackerTextBGFilterId = useRef(
    `tracker-text-bg-filter-${getEntityKey(entities?.[0])}-${
      randomHash.current
    }`
  );
  /**
   * Local Variables
   */
  const dataKey = dataType === 'number' ? 'value' : dataType;
  const margin = getMargins(hasComplementary);

  const chartPeriod = useMemo(() => {
    return period === 'customDate' ? 'week' : period;
  }, [period]);

  const width = useMemo(() => {
    // avoid negative widths.
    if (parentWidth === 0) return parentWidth;
    return round(parentWidth - margin.left - margin.right);
  }, [margin, parentWidth]);

  const height = useMemo(() => {
    // avoid negative heights.
    if (parentHeight === 0) return parentHeight;
    return round(parentHeight - margin.top - margin.bottom);
  }, [margin, parentHeight]);

  const numberOfDataPoints = useMemo(
    () => Math.max(...entities.map((entity: any) => entity.data.length)),
    [entities]
  );

  const showInteractiveElements = Boolean(
    !hasNoData && shouldTransitionIn && handleMouseClick && handleMouseHover
  );

  /**
   * Scales
   */
  const x = useTimeScale(entities, chartPeriod, [0, width - 45], true);
  const band = useBandScale(x, chartPeriod);

  const useYScale = (entities: any, key: string, h: number): any => {
    return useLinearScale(entities, key, [h, 0]);
  };

  const y = useYScale(entities, dataKey, height);
  /**
   * Get the y-position of the topmost vertical tick.
   */
  const firstTickY = useMemo(() => {
    const yTicks = (y as any).ticks(32);
    return round(y(last(yTicks)));
  }, [y]);

  /** Get metric type */
  const getMetricType = (entities: any): any =>
    entities.reduce((metrics: string[], entity: any) => {
      const metricType =
        entity?.breakdown?.indexOf('views') !== -1 ? 'views' : 'creations';
      metrics.push(metricType);
      return [...new Set(metrics)];
    }, []);
  const metricType = getMetricType(entities);

  const useStrictScale = (
    trends: any,
    dataKey: string,
    height: number
  ): any => {
    return trends.map((trend: any) => {
      const toValue = (d: any): any => d[dataKey];
      /** Get trends with the same metricType (views or creations) to get the proper y scale */
      const sameMetricTrends = trends.filter((entity: any) => {
        const metric =
          trend.breakdown.indexOf('views') !== -1 ? 'views' : 'creations';
        return entity.breakdown.indexOf(metric) !== -1;
      });
      const values = sameMetricTrends.flatMap((trend: any) =>
        trend.data.map(toValue)
      );
      const y = createLinearScale(values, [height, 0], 0);

      /** Path generators */
      const path = line()
        .x((d: any) => x(d.date))
        .y((d: any) => y(d[dataKey]))
        .defined((d) => isLineChartPathDefined(d, dataKey))
        .curve(curve);
      const gradient = area()
        .defined((d: any) => d[dataKey] !== null)
        .x((d: any) => x(d.date))
        .y0(() => height)
        .y1((d: any) => y(d[dataKey]))
        .curve(curve);
      return { ...trend, y, path, gradient };
    });
  };

  const metricEntities = useStrictScale(entities, dataKey, height);
  const addYFunction = (entity: any): any => ({
    ...entity,
    y: metricEntities.reduce(
      (prev: any, current: any) =>
        current?.breakdown === entity?.breakdown ? current.y : prev,
      null
    ),
  });
  const selectedTrends = (temporarySelectedEntities || selectedEntities).map(
    addYFunction
  );
  const selectedUpdatedEntities = selectedEntities.map(addYFunction);
  const ChartLinesComponent = ChartLines as any;
  const HitboxComponent = Hitbox as any;

  return (
    <>
      <defs>
        <clipPath id={clipPathId.current}>
          <rect x={0} y={0} width={width} height={height} />
        </clipPath>

        <filter
          id={trackerTextBGFilterId.current}
          x={-0.3}
          y={0}
          width="1.6"
          height="1.2"
          primitiveUnits="objectBoundingBox"
        >
          <feImage
            preserveAspectRatio="none"
            xlinkHref="data:image/svg+xml;charset=UTF-8,%3csvg width='10' height='1' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3clinearGradient id='filterGradient'%3e%3cstop offset='0%25' stop-color='white' stop-opacity='0' /%3e%3cstop offset='15%25' stop-color='white' stop-opacity='1' /%3e%3cstop offset='85%25' stop-color='white' stop-opacity='1' /%3e%3cstop offset='100%25' stop-color='white' stop-opacity='0' /%3e%3c/linearGradient%3e%3c/defs%3e%3crect x='0%25' y='0%25' width='100%25' height='100%25' fill='url(%23filterGradient)'/%3e%3c/svg%3e"
          />
          <feComposite in="SourceGraphic" />
        </filter>
      </defs>

      {chartPeriod === 'day' && (
        <InnerGroup transform={`translate(${margin.left}, 0)`}>
          <ThisWeek
            x={x as any}
            topMargin={margin.top}
            yPos={firstTickY}
            height={height}
            isSmallScreen={isSmallScreen}
            isComplementary={isComplementary}
          />
        </InnerGroup>
      )}

      {metricEntities && (
        <AxesInStrictDesign
          x={x}
          period={chartPeriod}
          isSmallScreen={isSmallScreen}
          hasNoData={hasNoData}
          sizes={{ width, height }}
          margin={margin}
          dataType={dataType}
          numberOfDataPoints={numberOfDataPoints}
          metricType={metricType}
          metricEntities={metricEntities}
        />
      )}

      {showInteractiveElements && (
        <HitboxComponent
          transform={`translate(${margin.left}, 0)`}
          size={{
            width,
            height: parentHeight,
          }}
          x={x}
          period={chartPeriod}
          handleMouseClick={handleMouseClick}
          handleMouseHover={handleMouseHover}
          handleDragChange={handleDragChange}
        />
      )}

      {metricEntities &&
        metricEntities.map((entity: any, index: number) => {
          const { path, y, gradient } = entity;
          const hasDashedGaps = entity.data.some(
            (trend: any) => trend.value === null
          );
          return (
            <InnerGroup
              transform={`translate(${margin.left}, ${margin.top})`}
              key={index}
            >
              <ChartLinesComponent
                trends={[entity]}
                colorScale={colorScale}
                x={x}
                y={y}
                dataType={dataType}
                dataKey={dataKey}
                fillFromLastNonNullValue={fillFromLastNonNullValue}
                width={width}
                height={height}
                path={path}
                gradient={gradient}
                hasDashedGaps={hasDashedGaps}
              />
            </InnerGroup>
          );
        })}

      {showInteractiveElements && (
        <InnerGroup transform={`translate(${margin.left}, ${margin.top})`}>
          {Boolean(temporarySelectedEntities) && (
            <Tracker
              band={band}
              clipPathId={clipPathId.current}
              colorScale={colorScale}
              dataKey={dataKey}
              hasComplementary={hasComplementary}
              height={height}
              isDragging={false}
              isGhostTracker={true}
              label={label}
              margin={margin}
              period={chartPeriod}
              selectedEntities={selectedUpdatedEntities}
              showTooltip={showTooltip}
              textBackgroundFilterId={trackerTextBGFilterId.current}
              tooltipRoot={tooltipRoot}
              width={width}
              x={x}
              y={y}
              yStart={0}
              isStrictDesign={isStrictDesign}
            />
          )}
          {metricEntities && (
            <Tracker
              band={band}
              clipPathId={clipPathId.current}
              colorScale={colorScale}
              dataKey={dataKey}
              hasComplementary={hasComplementary}
              height={height}
              isDragging={false}
              isGhostTracker={false}
              label={label}
              margin={margin}
              period={chartPeriod}
              selectedEntities={selectedTrends}
              showTooltip={showTooltip}
              textBackgroundFilterId={trackerTextBGFilterId.current}
              tooltipRoot={tooltipRoot}
              width={width}
              x={x}
              y={y}
              yStart={0}
              isStrictDesign={isStrictDesign}
            />
          )}
        </InnerGroup>
      )}
    </>
  );
};

export default LineChart;
